import { LogoListFieldsFragment } from '@src/generated/graphql';
import { useEffect, useState } from 'react';
import { DynamicLink, ImageWrapper } from 'src/components/shared';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { highlightBracketedText, highlightUnderscoreText } from 'src/utils/utils';

type TransformedImage = {
  src: string;
  alt: string;
  websiteUrl: string;
  websiteUrlAltText: string;
};

type MarqueeComponentProps = {
  content: LogoListFieldsFragment;
  batch?: boolean; 
  omnichannel?: boolean;
};

const TickerContent = ({ images }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isMobile) {
    return (
      <div className="marquee-desktop">
        <div className="marquee-content">
          {images.map((image, index) => (
            <DynamicLink
              key={index}
              url={image.websiteUrl ?? '#'}
              aria-label={image.websiteUrlAltText ?? ''}
            >
              <ImageWrapper 
                src={image.src} 
                alt={image.alt} 
                height={83} 
                className="marquee-logo-link"
              />
            </DynamicLink>
          ))}
        </div>
        <div className="marquee-content marquee-content-home">
          {images.map((image, index) => (
            <DynamicLink
              key={index}
              url={image.websiteUrl ?? '#'}
              aria-label={image.websiteUrlAltText ?? ''}
            >
              <ImageWrapper 
                src={image.src} 
                alt={image.alt} 
                height={83} 
                className="marquee-logo-link"
              />
            </DynamicLink>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="marquee-wrapper"> 
      <div className="marquee">
        <div className="marquee-group">
          {images.map((image, index) => (
            <DynamicLink
              key={index}
              url={image.websiteUrl ?? '#'}
              aria-label={image.websiteUrlAltText ?? ''}
              className="marquee-item"
            >
              <ImageWrapper 
                src={image.src} 
                alt={image.alt} 
                height={50} 
                className="marquee-logo-link"
              />
            </DynamicLink>
          ))}
        </div>
        <div className="marquee-group" aria-hidden="true">
          {images.map((image, index) => (
            <DynamicLink
              key={`duplicate-${index}`}
              url={image.websiteUrl ?? '#'}
              aria-label={image.websiteUrlAltText ?? ''}
              className="marquee-item"
            >
              <ImageWrapper 
                src={image.src} 
                alt={image.alt} 
                height={50} 
                className="marquee-logo-link"
              />
            </DynamicLink>
          ))}
        </div>
      </div>
    </div>
  );
};


const MarqueeComponent = ({ content , batch = false, omnichannel }: MarqueeComponentProps) => {
  const logos = content?.logosCollection?.items ?? [];
  const title = content?.title;
  const description = content?.descriptionJson;

  if (logos.length === 0) return null;

  const transformedImages: TransformedImage[] = logos.map(logo => ({
    src: logo?.logoImage?.url ?? '',
    alt: logo?.logoImage?.title ?? '',
    websiteUrl: logo?.websiteUrl ?? '',
    websiteUrlAltText: logo?.websiteUrlAltText ?? '',
  }));

  const extractPlainTextFromRichText = (node: any): string => {
    if (!node) return '';
    if (node.nodeType === 'text') {
      return node.value;
    }

    if (Array.isArray(node.content)) {
      return node.content.map(extractPlainTextFromRichText).join('\n');
    }

    return '';
  };

  return (
    <section className="marquee-component-block">
      <div className="container-fluid custom-spacer-col">
        <div className="row">
          <div className="col-md-12 col-sm-12">
            {batch ? (
              // <h3 className='batch-marquee-title'>
              //   {title}
              //   </h3>
              <h3
                className='batch-marquee-title'
                dangerouslySetInnerHTML={{
                  __html: batch
                    ? highlightBracketedText(title ?? "", "bold")
                    : highlightUnderscoreText(title ?? ""),
                }}
              />
            ) : (
              <div className='marquee-title'>{title}</div>
            )}
           
            {batch && description?.json && (
              <div className='marquee-description'>
                {omnichannel
                  ? (
                    <p
                      dangerouslySetInnerHTML={{
                        __html: highlightUnderscoreText(
                          extractPlainTextFromRichText(description.json)
                            .replace(/\\n|\/n|\n/g, '<br/>')
                        )
                      }}
                    />
                  ) : (
                    documentToReactComponents(description.json)
                  )}
              </div>
            )}

              <TickerContent images={transformedImages} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarqueeComponent;
